/**
 * ThreeWayCompareModal — the centerpiece of the Full Audit Wizard's Document
 * Verify step. Three columns:
 *   1) ENCODED data (line items / qty / cost as it was keyed into AgriBooks)
 *   2) Receipt photo gallery (ReceiptLightbox — zoom + drag, multiple images)
 *   3) "View on Phone" QR (reuses ViewQRDialog) so the auditor can cross-check
 *      the physical receipt on a phone.
 * Footer: Verify (PIN) — reuses AuthDialog (mode="pin", docType/docId) which has
 * built-in discrepancy capture (expected vs found qty + unit cost → value impact)
 * and writes to discrepancy_log via routes/verify.py.
 */
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../contexts/AuthContext';
import { formatPHP } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  ShieldCheck, Smartphone, FileText, Loader2, CheckCircle2, AlertTriangle, Image as ImageIcon, X,
} from 'lucide-react';
import AuthDialog from '../AuthDialog';
import ViewQRDialog from '../ViewQRDialog';
import ReceiptLightbox from './ReceiptLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// VQ doc_type → single-document fetch endpoint
const DOC_ENDPOINT = {
  purchase_order: (id) => `/purchase-orders/${id}`,
  expense: (id) => `/expenses/${id}`,
  branch_transfer: (id) => `/branch-transfers/${id}`,
};

function num(v) { const n = Number(v); return isFinite(n) ? n : 0; }

// Normalize line items across the 3 doc shapes into {name, qty, unit, cost, total}.
function normalizeLines(doc) {
  const items = doc?.items || doc?.line_items || [];
  return items.map(it => {
    const qty = it.quantity ?? it.qty ?? it.qty_received ?? it.qty_ordered ?? 0;
    const cost = it.unit_price ?? it.transfer_capital ?? it.unit_cost ?? it.cost ?? it.price ?? 0;
    return {
      name: it.product_name || it.description || it.name || '—',
      qty: num(qty),
      unit: it.unit || '',
      cost: num(cost),
      total: it.total != null ? num(it.total) : num(qty) * num(cost),
    };
  });
}

export default function ThreeWayCompareModal({ open, onClose, item, onVerified }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const docType = item?.doc_type;
  const docId = item?.id;

  const load = useCallback(async () => {
    if (!open || !docType || !docId) return;
    setLoading(true);
    try {
      const ep = DOC_ENDPOINT[docType]?.(docId);
      const res = ep ? await api.get(`${BACKEND_URL}/api${ep}`) : { data: null };
      setDoc(res.data);
    } catch { setDoc(null); }
    setLoading(false);
  }, [open, docType, docId]);

  useEffect(() => { load(); }, [load]);

  if (!open || !item) return null;

  const lines = normalizeLines(doc);
  const isExpense = docType === 'expense';
  const verified = !!item.verified;

  return (
    <>
      {createPortal(
        <div className="fixed inset-0 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9998, pointerEvents: 'auto' }}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
          data-testid="three-way-compare-overlay">
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-y-auto"
            style={{ pointerEvents: 'auto' }} data-testid="three-way-compare-modal">
            <button onClick={onClose} className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid="compare-close-x">
              <X size={14} className="text-slate-500" />
            </button>
            <div className="px-5 py-3.5 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2 text-base font-semibold text-slate-800">
                <ShieldCheck size={18} className="text-[#1A4D2E]" />
                Verify {item.label}
                {verified ? (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Verified</Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200">Unverified</Badge>
                )}
                {item.has_discrepancy && <Badge className="bg-red-100 text-red-700 border-red-200">Discrepancy</Badge>}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {item.party && <span>{item.party} · </span>}
                {item.ref && <span>Ref {item.ref} · </span>}
                {item.date} · {formatPHP(item.total)}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-x divide-slate-100">
            {/* Column 1 — Encoded */}
            <div className="p-4 min-h-[340px]" data-testid="compare-encoded">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
                <FileText size={12} /> Encoded in AgriBooks
              </p>
              {loading ? (
                <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 size={18} className="animate-spin" /></div>
              ) : isExpense ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-400">Category</span><span className="font-medium text-slate-700">{doc?.category || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Description</span><span className="font-medium text-slate-700 text-right max-w-[60%]">{doc?.description || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Payment</span><span className="font-medium text-slate-700">{doc?.payment_method || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Reference</span><span className="font-mono text-slate-700">{doc?.reference_number || '—'}</span></div>
                  <div className="flex justify-between border-t border-slate-100 pt-2 mt-2"><span className="text-slate-500 font-semibold">Amount</span><span className="font-bold text-[#1A4D2E]">{formatPHP(doc?.amount || item.total)}</span></div>
                </div>
              ) : lines.length === 0 ? (
                <p className="text-xs text-slate-400 py-8 text-center">No line items recorded.</p>
              ) : (
                <div className="text-xs">
                  <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 text-slate-400 border-b border-slate-100 pb-1.5">
                    <span className="font-medium">Item</span>
                    <span className="font-medium text-right">Qty</span>
                    <span className="font-medium text-right">Cost</span>
                    <span className="font-medium text-right">Total</span>
                  </div>
                  {lines.map((l, i) => (
                    <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 py-1.5 border-b border-slate-50">
                      <span className="text-slate-700 truncate">{l.name}</span>
                      <span className="text-right font-mono text-slate-600">{l.qty}{l.unit ? ` ${l.unit}` : ''}</span>
                      <span className="text-right font-mono text-slate-600">{formatPHP(l.cost)}</span>
                      <span className="text-right font-mono font-semibold text-slate-700">{formatPHP(l.total)}</span>
                    </div>
                  ))}
                  <div className="grid grid-cols-[1fr_auto] gap-x-3 pt-2">
                    <span className="text-right text-slate-500 font-semibold">Total</span>
                    <span className="text-right font-bold text-[#1A4D2E]">{formatPHP(item.total)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Column 2 — Photo gallery */}
            <div className="p-4 min-h-[340px] flex flex-col" data-testid="compare-gallery">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
                <ImageIcon size={12} /> Receipt Photo{item.attachment_count > 0 ? ` (${item.attachment_count})` : ''}
              </p>
              <div className="flex-1">
                <ReceiptLightbox recordType={docType} recordId={docId} />
              </div>
            </div>

            {/* Column 3 — View on Phone */}
            <div className="p-4 min-h-[340px] flex flex-col items-center justify-center text-center gap-3" data-testid="compare-phone">
              <span className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center"><Smartphone size={22} className="text-white" /></span>
              <div>
                <p className="text-sm font-semibold text-slate-700">View / attach on phone</p>
                <p className="text-xs text-slate-400 mt-1 max-w-[200px]">Open a read-only QR to inspect the photos full-screen, or snap &amp; attach a missing receipt.</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setQrOpen(true)} data-testid="compare-qr-phone">
                <Smartphone size={14} className="mr-1.5" /> View on Phone
              </Button>
              {item.missing_proof && (
                <div className="flex items-center gap-1.5 text-[11px] text-amber-600 mt-1">
                  <AlertTriangle size={12} /> No proof attached yet
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50">
            <p className="text-xs text-slate-400">
              {verified
                ? `Verified by ${item.verification_status === 'discrepancy' ? 'auditor (discrepancy logged)' : 'auditor'}`
                : 'Cross-check the encoded data against the receipt, then verify.'}
            </p>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onClose} data-testid="compare-close-btn">Close</Button>
              <Button size="sm" onClick={() => setAuthOpen(true)} data-testid="compare-verify-btn"
                className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">
                <CheckCircle2 size={14} className="mr-1.5" /> {verified ? 'Re-verify' : 'Verify (PIN)'}
              </Button>
            </div>
          </div>
          </div>
        </div>,
        document.body
      )}

      {/* PIN gate + discrepancy capture (reused) */}
      <AuthDialog
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        mode="pin"
        docType={docType}
        docId={docId}
        docLabel={item.label}
        context={`audit_verify_${docType}`}
        showDiscrepancy
        onVerified={(res) => { setAuthOpen(false); onVerified?.(res); }}
      />

      <ViewQRDialog
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        recordType={docType}
        recordId={docId}
        fileCount={item.attachment_count || 0}
      />
    </>
  );
}
